import { Autocomplete, Button, FormControl, Grid, SvgIcon, TextField } from '@mui/material';
import CountZonesTable from './CountZonesTable';
import { CountZonesResponse, Item } from '../../../app/services/api/generated';
import LoadingWheel from '../../../components/ui/LoadingWheel';
import { PlusCircle } from '../../../assets';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useContext, useState } from 'react';
import { UserContext } from '../../../components/shared/useUser';
import useItemSearch from '../hooks/useItemSearch';

type Props = { zones: CountZonesResponse[], storeID: string, loadData: (itemNumber?: string) => void, isLoading: boolean };

const MainBox = (props: Props) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const { items, isSearching, debouncedSearch } = useItemSearch();
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { hasPermissionTo } = useContext(UserContext);
  const hasRightToEdit: boolean = hasPermissionTo(['StockLocationWrite']) || hasPermissionTo(['StockCountAdmin']);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleItemChange = (_event: React.SyntheticEvent, value: Item | null) => {
    setSelectedItem(value);
    props.loadData(value?.itemNumber ?? undefined);
  };

  return (
    <Grid container
      spacing={4}
      sx={{ pt: '8px' }}>
      <Grid item
        xs={12}
        sm={4}
        sx={{ pb: '8px' }}>
        <FormControl fullWidth>
          <TextField
            label={t('search') + ':'}
            size='small'
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </FormControl>
      </Grid>
      <Grid item
        xs={12}
        sm={4}
        sx={{ pb: '8px' }}>
        <Autocomplete
          size='small'
          options={items}
          value={selectedItem}
          loading={isSearching}
          filterOptions={(x) => x}
          onInputChange={(_event, value) => debouncedSearch(value, props.storeID)}
          onChange={handleItemChange}
          getOptionKey={(option) => option.id ?? ''}
          getOptionLabel={(option) => (option && `${option.itemNumber} - ${option.description}`) ?? ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField {...params}
              label={t('searchByItem')}
            />
          )}
        />
      </Grid>
      <Grid item
        xs={12}
        sm={4}
        sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start' }}>
        <Button
          size='lg'
          onClick={() => navigate('/create-edit-count-zone')}
          startIcon={<SvgIcon><PlusCircle /></SvgIcon>}
          disabled={!hasRightToEdit}
        >
          {t('createNew')}
        </Button>
      </Grid>
      <Grid item
        xs={12}>
        {props.isLoading ? (
          <LoadingWheel />
        ) : (
          <CountZonesTable
            list={props.zones}
            storeID={props.storeID}
            loadData={() => props.loadData(selectedItem?.itemNumber)}
            searchTerm={searchTerm}
            isLoading={props.isLoading} />
        )}
      </Grid>
    </Grid>
  );
};

const CountZonesBox = (props: Props) => {
  return <MainBox {...props} />;
};

export default CountZonesBox;
